import { RefObject, useCallback, useMemo } from "react";

import BulkActions from "components/BulkActions";
import BulkActionsDrawer from "components/BulkActions/Drawer";
import BulkActionsDrawerActionSteps from "components/BulkActions/Drawer/ActionSteps";
import BulkActionsDrawerActionStepsHeader from "components/BulkActions/Drawer/ActionSteps/Header";
import BulkActionsDrawerActionStepsInfoBanner from "components/BulkActions/Drawer/ActionSteps/InfoBanner";
import BulkActionsDrawerResultsStep from "components/BulkActions/Drawer/ResultsStep";
import BulkActionsDrawerResultsStepHeader from "components/BulkActions/Drawer/ResultsStep/Header";
import BulkActionsDrawerResultsStepBody from "components/BulkActions/Drawer/ResultsStep/Body";
import BulkActionsDrawerResultsStepBodyEmptyTab from "components/BulkActions/Drawer/ResultsStep/Body/EmptyTab";
import BulkActionsDrawerResultsStepFooter from "components/BulkActions/Drawer/ResultsStep/Footer";
import BulkActionsFloatingBar from "components/BulkActions/FloatingBar";
import BulkActionsFloatingBarHeader from "components/BulkActions/FloatingBar/Header";
import BulkActionsFloatingBarSteps from "components/BulkActions/FloatingBar/Steps";
import { type BulkActionItemTriggerResult } from "components/BulkActions/helpers";
import {
  BulkActionItemID,
  BulkActionResultTabs,
  BulkActionsResult,
  BulkActionsStep,
  BulkActionsVariant,
} from "components/BulkActions/types";
import useBulkActions from "components/BulkActions/useBulkActions";
import useExecutionQueue from "components/BulkActions/useExecutionQueue";
import useAnalytics from "hooks/useAnalytics";
import BulkActionsPatternsActionsList from "components/BulkActionsPatterns/ActionsList";
import { useResultTabItems } from "components/BulkActions/useResultTabItems";
import { AnalyticsPageOrganization } from "hooks/useAnalytics/pages/organization";

import UserRolesBulkActionsConfirmActionForm from "./ConfirmActionForm";
import UserRolesBulkActionsConfirmationView from "./ConfirmationView";
import UserRolesBulkActionsResultItem from "./ResultItem";
import UserRolesBulkActionsSelectedItemsView from "./SelectedItemsView";
import {
  UserRolesBulkActionsAnalyticsPayload,
  UserRolesBulkActions as UserRolesBulkActionsEnum,
} from "./types";
import useUserRolesBulkActions from "./useUserRolesBulkActions";
import { UserRolesMutationVariables } from "./useUserRolesBulkActions/types";
import { UserViewBinding } from "../../types";

type UserRolesBulkActionsProps = {
  virtualizedListContainerRef?: RefObject<HTMLElement | null>;
  selectedSet: Set<BulkActionItemID>;
  userRolesMap: Map<BulkActionItemID, UserViewBinding>;
  onBulkResetAll: () => void;
  onItemDismiss: (id: BulkActionItemID) => void;
  onFinish: () => Promise<unknown>;
};

const UserRolesBulkActions = ({
  selectedSet,
  userRolesMap,
  onBulkResetAll,
  onItemDismiss,
  virtualizedListContainerRef,
  onFinish,
}: UserRolesBulkActionsProps) => {
  const selectedItemsCount = selectedSet.size;

  const userRolesBulkActions = useUserRolesBulkActions();
  const trackSegmentAnalyticsEvent = useAnalytics({
    page: AnalyticsPageOrganization.OrganizationUserRoles,
  });

  const {
    selectedBulkAction,
    resetSelectedAction,
    availableActions,
    itemsPerAction,
    actionsPerItem,
    selectedEntityItems,
    applicableSelectedEntityItems,
    skippedSelectedEntityItems,
  } = useBulkActions({
    actions: userRolesBulkActions,
    selectedSet,
    entityItemsMap: userRolesMap,
    analyticsPage: AnalyticsPageOrganization.OrganizationUserRoles,
  });

  const onItemTrigger = useCallback(
    (
      id: BulkActionItemID,
      mutationVariables: UserRolesMutationVariables
    ): Promise<BulkActionItemTriggerResult> => {
      const roleBinding = userRolesMap.get(id);
      if (!roleBinding || !selectedBulkAction) {
        return Promise.reject();
      }
      if (!selectedBulkAction.condition(roleBinding).result) {
        return Promise.resolve(BulkActionsResult.Skipped);
      }
      return selectedBulkAction.mutation(roleBinding, mutationVariables);
    },
    [userRolesMap, selectedBulkAction]
  );

  const {
    triggerExecution,
    bulkActionItemResults,
    stopExecution,
    stopExecutionForItem,
    bulkActionResultsMetadata,
    resetBulkActionsExecutionQueue,
  } = useExecutionQueue({
    onItemTrigger,
    onAllItemsFinished: onFinish,
    analyticsPage: AnalyticsPageOrganization.OrganizationUserRoles,
  });

  const { resultsTabItems, saveEntityMap, resetEntityMap } =
    useResultTabItems<UserViewBinding>(bulkActionItemResults);

  const handleClose = useCallback(() => {
    onBulkResetAll();
    resetSelectedAction();
    stopExecution();
    resetBulkActionsExecutionQueue();
    resetEntityMap();
  }, [
    onBulkResetAll,
    resetBulkActionsExecutionQueue,
    resetEntityMap,
    resetSelectedAction,
    stopExecution,
  ]);

  const handleOnConfirm = useCallback(
    (
      mutationVariables: UserRolesMutationVariables,
      analyticsPayload: UserRolesBulkActionsAnalyticsPayload
    ) => {
      if (selectedBulkAction) {
        const items = itemsPerAction.get(selectedBulkAction.key) || [];
        const skippedItems = skippedSelectedEntityItems.map((item) => item.id);
        triggerExecution(items, mutationVariables, skippedItems);
        saveEntityMap(userRolesMap);
        trackSegmentAnalyticsEvent("Bulk Actions - Action Confirmed", {
          applicableCount: items?.length || 0,
          notApplicableCount: skippedItems.length,
          action: selectedBulkAction.key,
          ...analyticsPayload,
        });
      }
    },
    [
      itemsPerAction,
      saveEntityMap,
      selectedBulkAction,
      skippedSelectedEntityItems,
      userRolesMap,
      trackSegmentAnalyticsEvent,
      triggerExecution,
    ]
  );

  const handleOnCancel = (variant: BulkActionsVariant) => () => {
    if (selectedBulkAction) {
      resetSelectedAction();
      const items = itemsPerAction.get(selectedBulkAction.key);
      trackSegmentAnalyticsEvent("Bulk actions - Action Cancelled", {
        applicableCount: items?.length || 0,
        notApplicableCount: skippedSelectedEntityItems.length,
        action: selectedBulkAction.key,
        view: variant,
      });
    }
  };

  const customConfirmationMessage = useMemo(() => {
    return selectedBulkAction?.key === UserRolesBulkActionsEnum.Delete &&
      selectedEntityItems.length === userRolesMap.size ? (
      <>
        Unassigning all roles will revoke the user's access to Spacelift. They will no longer be
        able to sign in.
        <br />
        <br />
        Are you sure you want to unassign all roles?
      </>
    ) : null;
  }, [selectedBulkAction?.key, selectedEntityItems.length, userRolesMap.size]);

  const handleContinueWith = useCallback(() => {
    resetSelectedAction();
    resetBulkActionsExecutionQueue();
  }, [resetSelectedAction, resetBulkActionsExecutionQueue]);

  return (
    <BulkActions
      selectedItemsCount={selectedItemsCount}
      onClose={handleClose}
      onContinueWith={handleContinueWith}
      isExecutingBulkActions={!!resultsTabItems[BulkActionResultTabs.Pending].count}
    >
      <BulkActionsFloatingBar virtualizedListContainerRef={virtualizedListContainerRef}>
        <BulkActionsFloatingBarHeader
          selectedItemsCount={selectedItemsCount}
          applicableItemsCount={
            selectedBulkAction?.key ? itemsPerAction.get(selectedBulkAction.key)?.length : 0
          }
          analyticsPage={AnalyticsPageOrganization.OrganizationUserRoles}
        />
        <BulkActionsFloatingBarSteps>
          {!selectedBulkAction && (
            <BulkActionsPatternsActionsList
              variant={BulkActionsVariant.FloatingBar}
              availableActions={availableActions}
              onEmptyActionsCancel={handleClose}
            />
          )}

          {selectedBulkAction && (
            <UserRolesBulkActionsConfirmActionForm
              variant={BulkActionsVariant.FloatingBar}
              action={selectedBulkAction}
              onConfirm={handleOnConfirm}
              onCancel={handleOnCancel(BulkActionsVariant.FloatingBar)}
              customConfirmationMessage={customConfirmationMessage}
            />
          )}
        </BulkActionsFloatingBarSteps>
      </BulkActionsFloatingBar>

      <BulkActionsDrawer>
        <BulkActionsDrawerActionSteps>
          <BulkActionsDrawerActionStepsHeader
            analyticsPage={AnalyticsPageOrganization.OrganizationUserRoles}
            step={
              !selectedBulkAction
                ? BulkActionsStep.ChooseAction
                : BulkActionsStep.ActionConfirmation
            }
          />
          <BulkActionsDrawerActionStepsInfoBanner />

          {!selectedBulkAction && (
            <UserRolesBulkActionsSelectedItemsView
              items={selectedEntityItems}
              onItemDismiss={onItemDismiss}
              availableActions={availableActions}
              actionsPerItem={actionsPerItem}
              onEmptyActionsCancel={handleClose}
            />
          )}
          {selectedBulkAction && (
            <UserRolesBulkActionsConfirmationView
              applicableItems={applicableSelectedEntityItems}
              skippedItems={skippedSelectedEntityItems}
              onItemDismiss={onItemDismiss}
              selectedBulkAction={selectedBulkAction}
              onConfirm={handleOnConfirm}
              onCancel={handleOnCancel(BulkActionsVariant.Drawer)}
              customConfirmationMessage={customConfirmationMessage}
            />
          )}
        </BulkActionsDrawerActionSteps>

        <BulkActionsDrawerResultsStep>
          {(props) => (
            <>
              <BulkActionsDrawerResultsStepHeader
                title={selectedBulkAction?.resultTitle || "Results"}
                analyticsPage={AnalyticsPageOrganization.OrganizationUserRoles}
              />
              <BulkActionsDrawerResultsStepBody {...props} counter={resultsTabItems}>
                {({ currentTab }) =>
                  resultsTabItems[currentTab].count === 0 ? (
                    <BulkActionsDrawerResultsStepBodyEmptyTab currentTab={currentTab} />
                  ) : (
                    resultsTabItems[currentTab].entitiesList.map(({ status, items }) =>
                      items.map((roleBinding) => (
                        <UserRolesBulkActionsResultItem
                          key={roleBinding.id}
                          tab={currentTab}
                          roleBinding={roleBinding}
                          resultsMetadata={bulkActionResultsMetadata}
                          stopExecution={() => stopExecutionForItem(roleBinding.id)}
                          status={status}
                        />
                      ))
                    )
                  )
                }
              </BulkActionsDrawerResultsStepBody>
              <BulkActionsDrawerResultsStepFooter
                {...props}
                bulkActionItemResults={bulkActionItemResults}
                stopExecution={stopExecution}
                analyticsPage={AnalyticsPageOrganization.OrganizationUserRoles}
              />
            </>
          )}
        </BulkActionsDrawerResultsStep>
      </BulkActionsDrawer>
    </BulkActions>
  );
};

export default UserRolesBulkActions;
